import React, { useState } from 'react';
import { MapPin, Loader, AlertCircle, CheckCircle } from 'lucide-react';

interface LocationCaptureProps {
  onCapture: (latitude: number, longitude: number) => void;
  prompt?: string;
  disabled?: boolean;
}

type CaptureState = 'idle' | 'loading' | 'success' | 'error';

const LocationCapture: React.FC<LocationCaptureProps> = ({
  onCapture,
  prompt = 'Please share your location',
  disabled = false
}) => {
  const [state, setState] = useState<CaptureState>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const handleCapture = () => {
    if (!navigator.geolocation) {
      setState('error');
      setErrorMessage('Geolocation is not supported by your browser');
      return;
    }

    setState('loading');
    setErrorMessage('');

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setState('success');

        // Automatically submit after successful capture
        setTimeout(() => {
          onCapture(latitude, longitude);
        }, 800); // Small delay to show success state
      },
      (error) => {
        setState('error');

        switch (error.code) {
          case error.PERMISSION_DENIED:
            setErrorMessage('Location permission denied. Please enable location access in your browser settings.');
            break;
          case error.POSITION_UNAVAILABLE:
            setErrorMessage('Location information is unavailable. Please try again.');
            break;
          case error.TIMEOUT:
            setErrorMessage('Location request timed out. Please try again.');
            break;
          default:
            setErrorMessage('An unknown error occurred while getting location.');
            break;
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  const getButtonContent = () => {
    switch (state) {
      case 'loading':
        return (
          <>
            <Loader size={18} className="animate-spin" />
            <span>Getting location...</span>
          </>
        );
      case 'success':
        return (
          <>
            <CheckCircle size={18} />
            <span>Location captured!</span>
          </>
        );
      case 'error':
        return (
          <>
            <AlertCircle size={16} />
            <span>Try again</span>
          </>
        );
      default:
        return (
          <>
            <MapPin size={16} />
            <span>Share Location</span>
          </>
        );
    }
  };

  return (
    <div className="space-y-3">
      {/* Prompt Text */}
      <div className="text-gray-800 text-sm px-3 py-3">
        {prompt}
      </div>

      {/* Location Capture Button */}
      <div
        onClick={() => {
          if (!disabled && state !== 'loading' && state !== 'success') {
            handleCapture();
          }
        }}
        role="button"
        tabIndex={disabled || state === 'loading' || state === 'success' ? -1 : 0}
        className={`w-full flex items-center justify-center gap-2 px-4 py-3 border-t border-gray-300 transition-all text-sm font-medium ${disabled || state === 'loading' || state === 'success'
          ? 'text-gray-400 opacity-50 cursor-not-allowed'
          : 'text-gray-700 cursor-pointer active:scale-[0.98]'
          } `}
      >
        {getButtonContent()}
      </div>


      {/* Error Message */}
      {state === 'error' && errorMessage && (
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle size={16} className="text-red-600 shrink-0 mt-0.5" />
          <p className="text-xs text-red-700">{errorMessage}</p>
        </div>
      )}
    </div>
  );
};

export default LocationCapture;
